import { ChevronLeft, ChevronRight } from 'lucide-react';
import { PortalDsButton, portalDs } from 'src/components/portal-design-system';
import { formatDisplayDateTime } from 'src/helpers/date.helpers';
import { cn } from 'src/lib/utils';
import type { PortalActivityLogEntry, PortalActivityLogResponse } from '../types/users.types';
import { formatPortalActivityActionLabel } from '../utils/portal-activity-actions.utils';
import { UsageBreakdownCell } from './usage-breakdown-cell';
import { resolvePortalUserUsage } from '../utils/portal-users.mappers';
import type { PortalUserDetail } from '../types/users.types';

const ACTIVITY_LOG_PAGE_SIZE = 10;

function ActivityLogEntry({ entry }: { entry: PortalActivityLogEntry }) {
  return (
    <li className="rounded-lg border border-[#EDEEF2] bg-[#F7F8FA] px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[13.5px] font-medium text-[#0D0F1A]">
            {formatPortalActivityActionLabel(entry.action)}
          </p>
          {entry.summary ? (
            <p className={cn(portalDs.bodySm, 'mt-1')}>
              {entry.summary}
            </p>
          ) : null}
          {entry.actor_email ? (
            <p className={cn(portalDs.caption, 'mt-1')}>
              by
              {' '}
              {entry.actor_email}
            </p>
          ) : null}
        </div>
        <time
          className="shrink-0 text-[12px] text-[#9CA3AF]"
          dateTime={entry.created_at}
        >
          {formatDisplayDateTime(entry.created_at)}
        </time>
      </div>
    </li>
  );
}

interface UserDetailUsageActivityTabProps {
  user: PortalUserDetail;
  activityLog: PortalActivityLogResponse;
  activityPage: number;
  onActivityPageChange: (page: number) => void;
  isActivityLoading?: boolean;
}

export function UserDetailUsageActivityTab({
  user,
  activityLog,
  activityPage,
  onActivityPageChange,
  isActivityLoading = false,
}: UserDetailUsageActivityTabProps) {
  const totalPages = Math.max(1, Math.ceil(activityLog.count / ACTIVITY_LOG_PAGE_SIZE));
  const hasPrevious = activityPage > 1;
  const hasNext = activityPage < totalPages;

  return (
    <div className="space-y-8">
      <section className={portalDs.sheetSection}>
        <h3 className={portalDs.sheetSectionTitle}>
          Usage (30 days)
        </h3>
        <UsageBreakdownCell usage={resolvePortalUserUsage(user)} />
      </section>

      <section className={portalDs.sheetSection}>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className={portalDs.sheetSectionTitle}>
            Activity log
          </h3>
          <span className={portalDs.rowCount}>
            {activityLog.count}
            {' '}
            {activityLog.count === 1 ? 'entry' : 'entries'}
          </span>
        </div>

        {isActivityLoading ? (
          <p className={portalDs.bodySm}>
            Loading activity…
          </p>
        ) : activityLog.results.length === 0 ? (
          <p className={portalDs.bodySm}>
            No activity recorded for this user yet.
          </p>
        ) : (
          <ul className="space-y-2">
            {activityLog.results.map((entry) => (
              <ActivityLogEntry
                key={entry.id}
                entry={entry}
              />
            ))}
          </ul>
        )}

        {activityLog.count > ACTIVITY_LOG_PAGE_SIZE ? (
          <div className="mt-4 flex items-center justify-between gap-3">
            <span className={portalDs.caption}>
              Page
              {' '}
              {activityPage}
              {' '}
              of
              {' '}
              {totalPages}
            </span>
            <div className="flex gap-2">
              <PortalDsButton
                type="button"
                variant="secondary"
                size="sm"
                disabled={!hasPrevious || isActivityLoading}
                onClick={() => onActivityPageChange(activityPage - 1)}
              >
                <ChevronLeft className="size-4" aria-hidden />
                Previous
              </PortalDsButton>
              <PortalDsButton
                type="button"
                variant="secondary"
                size="sm"
                disabled={!hasNext || isActivityLoading}
                onClick={() => onActivityPageChange(activityPage + 1)}
              >
                Next
                <ChevronRight className="size-4" aria-hidden />
              </PortalDsButton>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}

export { ACTIVITY_LOG_PAGE_SIZE };
